var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useReducer, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import LoadingScreen from '../components/LoadingScreen';
import { createApiClient } from '../api/createApiClient';
import { track } from '../analytics/analytics';
import { eventTypes } from '../analytics/constants';
import { resolveSelectedOrganization } from '../utils/selectedOrganization';
const initialState = {
    isInitialized: false,
    isAuthenticated: false,
    hasMetadata: false,
    user: {
        id: 'unknown',
        name: 'Unknown User',
        email: 'unknown',
        account: {
            subscription: {
                plan: {
                    name: undefined,
                },
            },
        },
    },
    permissions: {
        canAccessUserTools: false,
        canAccessTeamsV1: false,
        canAccessTeamsV2: false,
        canAccessMemories: false,
        canQueueMessages: false,
    },
    organizations: [],
};
const reducer = (state, action) => {
    var _a, _b;
    if (action.type === 'AUTH_STATE_CHANGED') {
        const { isAuthenticated, user } = action.payload;
        return Object.assign(Object.assign({}, state), { isAuthenticated, isInitialized: true, user });
    }
    if (action.type === 'USER_PROFILE_UPDATED') {
        return Object.assign(Object.assign({}, state), { user: Object.assign(Object.assign({}, state.user), { name: action.payload.name }) });
    }
    if (action.type === 'METADATA_LOADED') {
        const { plan, permissions, organizations, name } = action.payload;
        return Object.assign(Object.assign({}, state), { hasMetadata: true, user: Object.assign(Object.assign({}, state.user), { name: name || state.user.name, account: Object.assign(Object.assign({}, state.user.account), { subscription: Object.assign(Object.assign({}, state.user.account.subscription), { plan: {
                            name: plan,
                        } }) }) }), permissions: {
                canAccessUserTools: (permissions === null || permissions === void 0 ? void 0 : permissions.user_tools) || false,
                canAccessTeamsV1: (permissions === null || permissions === void 0 ? void 0 : permissions.teams_v1) || false,
                canAccessTeamsV2: (permissions === null || permissions === void 0 ? void 0 : permissions.teams_v2) || false,
                canAccessMemories: (_a = permissions === null || permissions === void 0 ? void 0 : permissions.view_memory) !== null && _a !== void 0 ? _a : false,
                canQueueMessages: (_b = permissions === null || permissions === void 0 ? void 0 : permissions.message_queueing) !== null && _b !== void 0 ? _b : false,
            }, organizations: organizations || [] });
    }
    return state;
};
export const AuthContext = createContext(Object.assign(Object.assign({}, initialState), { changeLoggedInUser: () => { }, updateUserName: () => { }, refreshMetadata: () => Promise.resolve(), logout: () => Promise.resolve(), authenticationManager: undefined }));
export const AuthProvider = (props) => {
    const { children, authenticationManager } = props;
    const [state, dispatch] = useReducer(reducer, initialState);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (state.isAuthenticated && props.torqCloudApiUrl) {
            getMe(authenticationManager, props.torqCloudApiUrl)
                .then(res => {
                var _a;
                setError(null);
                dispatch({
                    type: 'METADATA_LOADED',
                    payload: {
                        plan: res.data.plan,
                        permissions: res.data.permissions,
                        organizations: res.data.organizations,
                        name: res.data.name,
                    },
                });
                // Re-run identify now that org, plan, role, and name traits are
                // available. The initial identify in changeLoggedInUser fires before
                // metadata loads, so those traits would otherwise never reach Segment.
                const globalWithAnalytics = window;
                // Use the organization the user actually has selected, not simply the
                // first one, so multi-org users report the org they are working in.
                const currentOrg = resolveSelectedOrganization(res.data.organizations);
                (_a = globalWithAnalytics.analytics) === null || _a === void 0 ? void 0 : _a.identify(state.user.email, {
                    email: state.user.email,
                    name: res.data.name,
                    plan: res.data.plan,
                    organizationId: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.organization_id,
                    organizationName: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.organization_name,
                    organizationType: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.organization_level,
                    organizationRole: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.organization_role,
                });
            })
                .catch(err => {
                setError(err);
                console.error('Error loading me', err);
            });
        }
    }, [authenticationManager, props.torqCloudApiUrl, state.isAuthenticated, state.user.email]);
    const refreshMetadata = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!props.torqCloudApiUrl)
            return;
        try {
            const res = yield getMe(authenticationManager, props.torqCloudApiUrl);
            setError(null);
            dispatch({
                type: 'METADATA_LOADED',
                payload: {
                    plan: res.data.plan,
                    permissions: res.data.permissions,
                    organizations: res.data.organizations,
                    name: res.data.name,
                },
            });
        }
        catch (err) {
            setError(err);
        }
    }), [authenticationManager, props.torqCloudApiUrl]);
    const updateUserName = useCallback((name) => {
        dispatch({
            type: 'USER_PROFILE_UPDATED',
            payload: { name },
        });
    }, []);
    const changeLoggedInUser = useCallback((user) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        ;
        window.analytics.identify((_a = user.attributes) === null || _a === void 0 ? void 0 : _a.email, {
            email: (_b = user.attributes) === null || _b === void 0 ? void 0 : _b.email,
        });
        dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
                isAuthenticated: true,
                user: {
                    id: user.username,
                    email: (_c = user.attributes) === null || _c === void 0 ? void 0 : _c.email,
                    name: user.displayName || ((_d = user.attributes) === null || _d === void 0 ? void 0 : _d.email),
                    account: {
                        subscription: {
                            plan: {
                                name: undefined,
                            },
                        },
                    },
                },
            },
        });
    }), []);
    useEffect(() => {
        const loadCurrentUser = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const user = yield authenticationManager.currentAuthenticatedUser();
                if (user) {
                    changeLoggedInUser(user).catch(err => {
                        console.error('Error changing logged in user', err);
                    });
                }
                else {
                    dispatch({
                        type: 'AUTH_STATE_CHANGED',
                        payload: Object.assign({}, initialState),
                    });
                }
            }
            catch (err) {
                console.error(err);
                toast.error('An error occurred while loading the current user');
                dispatch({
                    type: 'AUTH_STATE_CHANGED',
                    payload: Object.assign({}, initialState),
                });
            }
        });
        void loadCurrentUser();
    }, [authenticationManager, changeLoggedInUser, dispatch]);
    const logout = (...args_1) => __awaiter(void 0, [...args_1], void 0, function* (source = 'app') {
        // userId is auto-attached from the shared analytics context, so it is not
        // passed here by hand.
        track(eventTypes.USER_LOGOUT, { source });
        yield authenticationManager.signOut();
        dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: Object.assign({}, initialState),
        });
    });
    // Wait until either we can confirm that the user is not authenticated, or
    // they are authenticated *and* we have successfully fetched metadata, or
    // torqCloudApiUrl is null, because in that scenario (used by TorqCloud React
    // App) we don't need to fetch metadata. This whole thing is a broken
    // abstraction, because the AuthContext for TorqCloud UI and TorqAgent UI are
    // fundamentally different.
    const renderChildren = state.isInitialized && (!props.torqCloudApiUrl || !state.isAuthenticated || state.hasMetadata);
    if (error) {
        throw error;
    }
    return (_jsx(AuthContext.Provider, { value: Object.assign(Object.assign({}, state), { changeLoggedInUser,
            updateUserName,
            refreshMetadata,
            logout,
            authenticationManager }), children: renderChildren ? children : _jsx(LoadingScreen, {}) }));
};
function getMe(authenticationManager, torqCloudApiUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const torqCloudApiClient = createApiClient(authenticationManager, torqCloudApiUrl, {
            'Content-type': 'application/json',
        });
        return torqCloudApiClient.get('/api/v1/users/me/', { withCredentials: true });
    });
}
//# sourceMappingURL=AuthContext.js.map